import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form';
import {
  attachedHyphensDate,
  isNationIncluded,
  sanitizeInput,
} from '@repo/utils';
import {
  ACCOUNT_INFO_SECOND,
  KOR_BANK_LIST,
  MAIN_NATIONS,
  DEFAULT_SWIFT_CODE_NATIONS,
} from '@repo/constants';
import {
  ICON_CHEVRON_DOWN_GRAY,
  ICON_FLAG_JPN,
  ICON_FLAG_KOR,
} from '@repo/assets';
import { Button, Icon } from '@successmode-ktp/kui';
import { FlexWrap, InfoJPN, Input, SwiftCode } from '@repo/components';
import { AccountInfo } from '@repo/types';

type Props = {
  register: UseFormRegister<AccountInfo>;
  getValues: UseFormGetValues<AccountInfo>;
  setValue: UseFormSetValue<AccountInfo>;
  reset: UseFormReset<AccountInfo>;
  errors?: FieldErrors;
  selectedAccountType: string;
  setSelectedAccountType: Dispatch<SetStateAction<string>>;
  disabled?: boolean;
  bankName?: string;
  bankAccount?: string;
  isRegister?: boolean;
  isMedical?: boolean;
  nationality: string;
  isSwiftCodeRequired: boolean;
  onVerifyKorAccount: any;
  isLoading: boolean;
};

export const InfoFormBasic = ({
  register,
  getValues,
  setValue,
  reset,
  errors,
  disabled = false,
  bankName,
  bankAccount,
  selectedAccountType,
  setSelectedAccountType,
  nationality,
  isSwiftCodeRequired,
  onVerifyKorAccount,
  isLoading,
}: Props) => {
  const isKorToJpnLocalStorage = localStorage.getItem('isKorToJpn');
  const [isVerified, setIsVerified] = useState(false);
  const [account, setAccount] = useState(bankAccount);
  const [isKorToJpn, setIsKorToJpn] = useState(false);

  useEffect(() => {
    if (isKorToJpnLocalStorage) {
      setIsKorToJpn(JSON.parse(isKorToJpnLocalStorage));
    }
  }, [isKorToJpnLocalStorage]);

  useEffect(() => {
    setAccount(bankAccount);
    setIsVerified(!!bankAccount);
  }, [bankAccount]);

  useEffect(() => {
    if (bankName !== '없음') {
      return;
    }
    setValue('bankAccount', '');
  }, [bankName]);

  const verifyAccount = () => {
    const bank = KOR_BANK_LIST.find((bank) => bank.name === bankName);
    const successCount = localStorage.getItem('korAccountSuccess') || 0;
    if (!account || !bank) {
      return alert('계좌번호를 입력해주세요.');
    }
    if (account === getValues('bankAccount')) {
      return alert('이미 인증된 계좌번호입니다.');
    }
    if (+successCount >= 5) {
      return alert('5회 이상 재시도 했습니다. 다시 로그인 해주세요.');
    }
    onVerifyKorAccount(
      { account_num: account, bank_code: bank.code },
      {
        onSuccess: ({ 계좌실명: accountName }: any) => {
          setValue('accountHolder', accountName);
          setValue('bankAccount', account);
          setIsVerified(true);
          const successCount = localStorage.getItem('korAccountSuccess') || 0;
          localStorage.setItem('korAccountSuccess', String(+successCount + 1));
        },
        onError: ({ data }: any) => {
          if (data.code === 'AP0002') {
            alert('인증에 실패했습니다. 은행과 계좌번호를 확인해주세요.');
            setIsVerified(false);
          }
        },
      },
    );
  };

  const clearForm = () => {
    const values = getValues();
    const emptyValues = Object.keys(values).reduce((acc, key) => {
      //@ts-ignore
      acc[key] = '';
      return acc;
    }, {});
    reset(emptyValues);
  };

  const changeNationToJPN = (value: boolean) => {
    localStorage.setItem('isKorToJpn', JSON.stringify(value));
    setIsKorToJpn(value);
    clearForm();
  };

  if (isKorToJpn) {
    return (
      <>
        <Button
          theme='mono'
          size='lg'
          onClick={() => changeNationToJPN(false)}
          style={{ marginBottom: '20px' }}
          disabled={disabled}
        >
          <AccountChange>
            <img src={ICON_FLAG_KOR} alt='' width={24} />
            한국계좌로 송금받기
            <IconRight />
          </AccountChange>
        </Button>
        <InfoJPN
          register={register}
          getValues={getValues}
          setValue={setValue}
          errors={errors}
          disabled={disabled}
          selectedAccountType={selectedAccountType}
          setSelectedAccountType={setSelectedAccountType}
        />
        <Input
          {...register('firstName', {
            required: 'This is a required item.',
            pattern: {
              value: ACCOUNT_INFO_SECOND.JPN.firstName.pattern,
              message: ACCOUNT_INFO_SECOND[nationality].firstName.message,
            },
          })}
          error={errors?.firstName}
          label={ACCOUNT_INFO_SECOND.JPN.firstName.label}
          placeholder={ACCOUNT_INFO_SECOND.JPN.firstName.placeholder}
          margin={[24, 0, 24]}
          disabled={disabled}
          isRequired
        />
        <Input
          {...register('lastName', {
            required: 'This is a required item.',
            pattern: {
              value: ACCOUNT_INFO_SECOND.JPN.secondName.pattern,
              message: ACCOUNT_INFO_SECOND.JPN.secondName.message,
            },
          })}
          error={errors?.lastName}
          label={ACCOUNT_INFO_SECOND.JPN.secondName.label}
          placeholder={ACCOUNT_INFO_SECOND.JPN.secondName.placeholder}
          margin={[0, 0, 24]}
          disabled={disabled}
          isRequired
        />
        <Input
          {...register('firstNameEng', {
            required: 'This is a required item.',
            pattern: {
              value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
              message: 'Please enter only English and numbers.',
            },
          })}
          error={errors?.firstNameEng}
          label='受取人名 (姓)'
          placeholder='Surname in English'
          margin={[0, 0, 24]}
          disabled={disabled}
          isRequired
        />
        <Input
          {...register('lastNameEng', {
            required: 'This is a required item.',
            pattern: {
              value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
              message: 'Please enter only English and numbers.',
            },
          })}
          error={errors?.lastNameEng}
          label='受取人名 (名)'
          placeholder='Given names in English'
          margin={[0, 0, 24]}
          disabled={disabled}
          isRequired
        />
        <Input
          {...register('birth', {
            required: 'This is a required item.',
          })}
          error={errors?.birth}
          label={ACCOUNT_INFO_SECOND.JPN.birth.label}
          placeholder='e.g. 1990-05-10'
          onChange={(e) =>
            setValue('birth', attachedHyphensDate(e.target.value))
          }
          maxLength={10}
          margin={[0, 0, 24]}
          disabled={disabled}
          isRequired
        />
        <Input
          {...register('phoneNumber', {
            required: 'This is a required item.',
            pattern: {
              value: /^\d+$/,
              message: 'Please fill in the number only.',
            },
          })}
          type='number'
          error={errors?.phoneNumber}
          label={ACCOUNT_INFO_SECOND.JPN.phoneNumber.label}
          placeholder='Phone Mobile Number'
          margin={[0, 0, 24]}
          disabled={disabled}
          isRequired
        />
      </>
    );
  }

  return (
    <>
      {/* 기본 스위프트 코드 국가 목록에 없는 경우 스위프트 코드 입력 필요 */}
      {!isNationIncluded(DEFAULT_SWIFT_CODE_NATIONS, nationality) &&
        isSwiftCodeRequired && (
          <SwiftCodeWrapper>
            <SwiftCode
              register={register}
              disabled={disabled}
              errors={errors}
            />
          </SwiftCodeWrapper>
        )}
      {!isNationIncluded(MAIN_NATIONS, nationality) && isSwiftCodeRequired && (
        <>
          <Input
            {...register('stateProvinceRegion', {
              required: 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z][a-zA-Z\s]*$/,
                message: 'Please enter only English',
              },
            })}
            label='Country'
            isRequired
            placeholder='Country (English only)'
            margin={[20, 0, 0]}
            error={errors?.stateProvinceRegion}
            disabled={disabled}
          />
          <Input
            {...register('city', {
              required: 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z][a-zA-Z\s]*$/,
                message: 'Please enter only English',
              },
            })}
            label='City'
            isRequired
            placeholder='City (English only)'
            margin={[20, 0, 0]}
            error={errors?.city}
            disabled={disabled}
          />
          <Input
            {...register('address', {
              required: 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                message: 'Please enter only English and numbers.',
              },
              onChange: (e) =>
                setValue('address', sanitizeInput(e.target.value)),
            })}
            label='Street Name & Number'
            isRequired
            placeholder='Street Name & Number'
            margin={[20, 0, 0]}
            error={errors?.address}
            disabled={disabled}
          />
          <Input
            {...register('addressDetail2', {
              required: 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                message: 'Please enter only English and numbers.',
              },
              onChange: (e) =>
                setValue('addressDetail2', sanitizeInput(e.target.value)),
            })}
            label='APT, UNIT or SUITE'
            isRequired
            placeholder='APT, UNIT or SUITE'
            margin={[20, 0, 0]}
            error={errors?.addressDetail2}
            disabled={disabled}
          />
          <Input
            {...register('zipPostalCode', {
              required: 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                message: 'Please enter only English and numbers.',
              },
              onChange: (e) =>
                setValue('zipPostalCode', sanitizeInput(e.target.value)),
            })}
            label='Post code'
            isRequired
            placeholder='Post code'
            margin={[20, 0, 20]}
            error={errors?.zipPostalCode}
            disabled={disabled}
          />
        </>
      )}
      <Input
        {...register(
          nationality !== 'THA' && ACCOUNT_INFO_SECOND[nationality]
            ? 'firstName'
            : 'lastName',
          {
            required:
              nationality === 'CHN'
                ? '这是必填项。'
                : 'This is a required item.',
            pattern: {
              value: ACCOUNT_INFO_SECOND[nationality]
                ? ACCOUNT_INFO_SECOND[nationality].firstName.pattern
                : /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
              message: ACCOUNT_INFO_SECOND[nationality]
                ? ACCOUNT_INFO_SECOND[nationality].firstName.message
                : 'Please enter only English and numbers.',
            },
          },
        )}
        error={
          nationality !== 'THA' && ACCOUNT_INFO_SECOND[nationality]
            ? errors?.firstName
            : errors?.lastName
        }
        label={
          ACCOUNT_INFO_SECOND[nationality]
            ? ACCOUNT_INFO_SECOND[nationality].firstName.label
            : 'First Name'
        }
        placeholder={
          ACCOUNT_INFO_SECOND[nationality]
            ? ACCOUNT_INFO_SECOND[nationality].firstName.placeholder
            : 'First name in English'
        }
        margin={[0, 0, 24]}
        disabled={disabled}
        isRequired
      />
      <Input
        {...register(
          nationality !== 'THA' && ACCOUNT_INFO_SECOND[nationality]
            ? 'lastName'
            : 'firstName',
          {
            required:
              nationality === 'CHN'
                ? '这是必填项。'
                : 'This is a required item.',
            pattern: {
              value: ACCOUNT_INFO_SECOND[nationality]
                ? ACCOUNT_INFO_SECOND[nationality].secondName.pattern
                : /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
              message: ACCOUNT_INFO_SECOND[nationality]
                ? ACCOUNT_INFO_SECOND[nationality].secondName.message
                : 'Please enter only English and numbers.',
            },
          },
        )}
        error={
          nationality !== 'THA' && ACCOUNT_INFO_SECOND[nationality]
            ? errors?.lastName
            : errors?.firstName
        }
        label={
          ACCOUNT_INFO_SECOND[nationality]
            ? ACCOUNT_INFO_SECOND[nationality].secondName.label
            : 'Last Name'
        }
        placeholder={
          ACCOUNT_INFO_SECOND[nationality]
            ? ACCOUNT_INFO_SECOND[nationality].secondName.placeholder
            : 'Last name in English'
        }
        margin={[0, 0, 24]}
        disabled={disabled}
        isRequired
      />
      {(nationality === 'CHN' || nationality === 'JPN') && (
        <Fragment>
          <Input
            {...register('firstNameEng', {
              required:
                nationality === 'CHN'
                  ? '这是必填项。'
                  : 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                message:
                  nationality === 'CHN'
                    ? '请仅使用英语。'
                    : 'Please enter only English and numbers.',
              },
            })}
            error={errors?.firstNameEng}
            label={nationality === 'CHN' ? '姓 (拼音)' : '受取人名 (姓)'}
            placeholder='Surname in English'
            margin={[0, 0, 24]}
            disabled={disabled}
            isRequired
          />
          <Input
            {...register('lastNameEng', {
              required:
                nationality === 'CHN'
                  ? '这是必填项。'
                  : 'This is a required item.',
              pattern: {
                value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                message:
                  nationality === 'CHN'
                    ? '请仅使用英语。'
                    : 'Please enter only English and numbers.',
              },
            })}
            error={errors?.lastNameEng}
            label={nationality === 'CHN' ? '名字 (拼音)' : '受取人名 (名)'}
            placeholder='Given names in English'
            margin={[0, 0, 24]}
            disabled={disabled}
            isRequired
          />
        </Fragment>
      )}

      <Input
        {...register('birth', {
          required:
            nationality === 'CHN' ? '这是必填项。' : 'This is a required item.',
        })}
        error={errors?.birth}
        label={
          ACCOUNT_INFO_SECOND[nationality]
            ? ACCOUNT_INFO_SECOND[nationality].birth.label
            : 'Date of Birth'
        }
        placeholder={nationality === 'KOR' ? '예) 1993-05-11' : '1993-05-11'}
        onChange={(e) => setValue('birth', attachedHyphensDate(e.target.value))}
        maxLength={10}
        margin={[0, 0, 24]}
        disabled={disabled}
        isRequired
      />

      <Input
        {...register('phoneNumber', {
          required:
            nationality === 'CHN' ? '这是必填项。' : 'This is a required item.',
          pattern: {
            value: /^\d+$/,
            message:
              nationality === 'CHN'
                ? '请仅填写号码。'
                : nationality === 'KOR'
                  ? '숫자만 입력해주세요.'
                  : 'Please fill in the number only.',
          },
        })}
        type='number'
        error={errors?.phoneNumber}
        label={
          ACCOUNT_INFO_SECOND[nationality]
            ? ACCOUNT_INFO_SECOND[nationality].phoneNumber.label
            : 'Mobile Number'
        }
        placeholder={
          nationality === 'KOR' ? '예) 01012341234' : 'Mobile Number'
        }
        margin={[0, 0, 24]}
        disabled={disabled}
        isRequired
      />
      {nationality === 'KOR' && (
        <>
          <LabelWrapper>
            <Label>계좌번호</Label>
            <RequiredDot>*</RequiredDot>
          </LabelWrapper>
          <BankWrap>
            <ArrowIcon src={ICON_CHEVRON_DOWN_GRAY} />
            <Select
              {...register('bank')}
              disabled={disabled}
              isSelected={Boolean(getValues('bank'))}
              onChange={(e) => {
                setValue('accountHolder', '');
                setValue('bank', e.target.value);
              }}
            >
              <Option value='' hidden>
                은행을 선택해주세요.
              </Option>
              {KOR_BANK_LIST.map((bank) => (
                <Option
                  key={bank.code}
                  value={bank.name}
                  selected={bank.name === '없음'}
                >
                  {bank.name}
                </Option>
              ))}
            </Select>
          </BankWrap>
          {bankName !== '없음' && (
            <AccountWrapper>
              <Input
                placeholder='계좌번호를 입력해주세요.'
                disabled={disabled || isVerified}
                error={errors?.bankAccount}
                margin={[8, 0, 8]}
                onChange={(e) => setAccount(e.target.value)}
                value={account}
              />
              {!isVerified ? (
                <Button
                  type='button'
                  disabled={disabled}
                  loading={isLoading}
                  onClick={verifyAccount}
                  style={{ width: '100px', height: '50px' }}
                >
                  계좌인증
                </Button>
              ) : (
                <Button
                  type='button'
                  disabled={disabled}
                  onClick={() => setIsVerified(false)}
                  style={{ width: '100px', height: '50px' }}
                >
                  수정하기
                </Button>
              )}
            </AccountWrapper>
          )}
          <Input
            placeholder={bankName === '없음' ? '예) 미국' : '예금주명'}
            {...register('accountHolder', {
              required:
                bankName === '없음'
                  ? '국가명을 입력해주세요'
                  : '계좌인증을 해주세요',
              pattern: {
                value: /^\S[가-힣]*$/,
                message: '한글로 입력해주세요.',
              },
            })}
            disabled={disabled || bankName !== '없음'}
            error={!isVerified ? errors?.accountHolder : undefined}
            margin={[8, 0, 24]}
          />
          <WarningText>
            * 본인 명의의 한국 계좌가 없을 경우, 가족 또는 지인의 계좌정보를
            입력해주시면 송금이 가능합니다.
          </WarningText>
          <Button
            theme='mono'
            size='lg'
            onClick={() => changeNationToJPN(true)}
            disabled={disabled}
          >
            <AccountChange>
              <img src={ICON_FLAG_JPN} alt='' width={24} />
              일본계좌로 송금받기
              <IconRight />
            </AccountChange>
          </Button>
          <GuideText>
            ※ 해외 계좌로 송금 받기 원하실 경우에는 은행을 ‘없음’으로 선택 후
            송금 받기 원하는 국가명을 입력해주세요. 입력하신 이메일로 출국
            확인이 완료된 후 7일 이내에 안내 메일을 발송해 드립니다. 해외 계좌
            송금시 수수료가 발생 됩니다.
          </GuideText>
        </>
      )}
    </>
  );
};

const SwiftCodeWrapper = styled.div`
  margin-bottom: 20px;
`;

const WarningText = styled.p`
  color: var(--KTP-Warning-100, #ed0828);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.24px;
  margin-bottom: 12px;
`;

const GuideText = styled.p`
  color: #a7a7a7;
  font-size: 12px;
  line-height: 20px;
  font-weight: 400;
  margin: 12px 0 40px;
  @media ${(props) => props.theme.mobile} {
    margin: 20px 0 20px;
  }
`;

const BankWrap = styled(FlexWrap)`
  position: relative;
`;

const ArrowIcon = styled.img`
  width: 24px;
  height: 24px;
  position: absolute;
  right: 13px;
  bottom: 15px;
`;
const Option = styled.option``;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 8px;
`;
const Label = styled.label`
  font-size: 18px;
  line-height: 26px;
  font-weight: 400;
  @media ${(props) => props.theme.mobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;
const RequiredDot = styled.div`
  color: #f13e4b;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  margin-left: 4px;
  padding-bottom: 6px;
`;

const AccountWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const AccountChange = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 0 20px;
  font-size: 14px;
`;

const IconRight = styled(Icon.ChevronRight)`
  margin-left: auto;
`;

const Select = styled.select<{
  isSelected?: boolean;
}>`
  width: 100%;
  border: 1px solid #cbccce;
  cursor: pointer;
  padding: 14px 13px;
  border-radius: 4px;
  appearance: none;
  font-weight: 400;
  line-height: 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.isSelected ? '#3a3b3e' : '#CBCCCE')};
  cursor: default;
`;
